<template>
  <v-layout class="sidebar-history">
    <v-flex class="text-left">
      <v-layout wrap>
        <v-flex
          v-if="showNewNoteOption"
          :class="{'note-header-is-sidebar': isSidebar}"
          :pa-6="isSidebar"
          :bb="isSidebar"
          xs12
        >
          <v-textarea
            v-model="newNote"
            hide-details
            outlined
            class="fp-input-outline surface"
            :placeholder="I18NGetter().useHistoryElements.TAKE_NOTE"
            :rows="1"
          >
            <template slot="append">
              <v-flex class="pr-0 pt-0 pb-4">
                <v-btn
                  class="mx-0"
                  depressed
                  color="primary"
                  :disabled="!newNote"
                  @click="addNote">
                  {{ I18NGetter().useHistoryElements.SEND }}
                </v-btn>
              </v-flex>
            </template>
          </v-textarea>
        </v-flex>
        <v-layout wrap>
          <v-flex xs12 :pa-6="isSidebar">
            <v-layout wrap v-if="historyList && historyList.length > 0">
              <v-flex v-for="(historyElement, index) in historyList" :key="'historyElement' + index" xs12>
                <template v-if="historyElement.createdDate">
                  <template v-for="(config, index) in TimelineDictionary">
                    <span
                      v-if="isTimelineMatch(historyElement, config)"
                      :key="index"
                      class="timeline-span py-6 pl-2 body-2 font-weight-medium"
                    >
                      {{ config.label() }}
                    </span>
                  </template>
                </template>
                <fp-message class="pl-1" :message="historyElement" />
              </v-flex>
                <v-flex>
                  <v-pagination
                    v-if="!!filterOptions && !loadMoreButton"
                    v-model="filterOptions.page"
                    :length="filterOptions.totalPagesNumber"
                    @input="$emit('fetchHistoryElements')"
                  />
                  <div class="pb-4" v-if="loadMoreButton && !!filterOptions && filterOptions.page < filterOptions.totalPagesNumber">
                    <fp-btn text @click="$emit('fetchHistoryElements')">
                      {{ I18NGetter().useHistoryElements.SHOW_MORE }} <v-icon size="24" class="ml-2">mdi-chevron-down</v-icon>
                    </fp-btn>
                  </div>
                </v-flex>
            </v-layout>
            <p v-else-if="notesOnly" class="subtitle-1 font-weight-bold text-center">
              {{ I18NGetter().useHistoryElements.NO_NOTES }}</p>
            <p v-else class="subtitle-1 font-weight-bold text-center">{{
                I18NGetter().useHistoryElements.NO_HISTORY
              }}</p>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {HistoryElement} from '@/models/HistoryElement';
import {FpMessageType} from '@/modules/deals/enums/FpMessageType';
import {isEqual, chain} from 'lodash-es';
import FpMessage from '@/components/messages/FpMessage.vue';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {BaseFilter} from '@/models/Filter';
import { useInstance } from '@/composables/useInstance';
import { TimelineDictionary, TimelineConfig} from '@/commons/dictionaries/TimelineDictionary';
const {$snackbarService,} = useInstance();

type FilterPagination = Pick<BaseFilter, 'page' | 'totalPagesNumber'>;

interface Props {
  showNewNoteOption?: boolean;
  chat?: boolean;
  notesOnly?: boolean;
  last?: number;
  historyElements: HistoryElement<any>[];
  dealId?: number;
  loanApplicationId?: number;
  isSidebar?: boolean;
  filterOptions?: FilterPagination;
  loadMoreButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showNewNoteOption: true,
  chat: false,
  notesOnly: false,
  last: 0,
  historyElements: () => [],
  isSidebar: false,
  loadMoreButton: false,
});

const emit = defineEmits<{
  (e: 'click:new-note', newNote: string, dealId: number | undefined): void,
  (e: 'fetchHistoryElements'): void,
}>();

const newNote = ref<string>('');
//@Warning   <template v-if="historyElement.createdDate"> stąd date.createdDate!

const isTimelineMatch = (
  historyElement: HistoryElement<any>,
  config: TimelineConfig
): boolean => {
  let elements = props.historyElements;
  
  if (config.filterPrevious) {
    elements = elements.filter(el => config.filterPrevious!(el.createdDate!));
  }
  
  return isEqual(
    historyElement,
    elements.find(date => config.check(date.createdDate!))
  );
};

const historyList = computed<HistoryElement<any>[]>(() => {
  let list: HistoryElement<any>[] = props.historyElements;
  if (props.last) {
    list = list.slice(0, props.last);
  }

  if (!props.chat) {
    return list;
  }

  return chain<HistoryElement<any>>(list)
    .filter(historyElement => historyElement.type === FpMessageType.NOTE_ADDED)
    .uniq()
    .value();
});

function addNote(): void {
  if (newNote.value.length) {
    emit('click:new-note', newNote.value, props.dealId ? props.dealId : props.loanApplicationId);
    newNote.value = '';
  } else {
    $snackbarService.openWarningSnackbar(I18NGetter().useHistoryElements.WARNING_SNACKBAR_ADD_NOTE);
  }
}
</script>

<style scoped lang="scss">

.note-header-is-sidebar {
  position: relative;
  z-index: 2;
  width: 100%;
  top: 0;
  background: var(--token-primary-surface);
}

p {
  line-height: 24px;
  margin-bottom: 8px;
  font-size: 16px;
  margin-top: 8px;
}

.timeline-span {
  line-height: 24px;
}
</style>
